import {
  KdsCallout,
  KdsModal,
  KdsText,
  KdsTextArea,
} from "@kazaar-platform/design-system";
import { RiErrorWarningLine } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { FormProvider, useForm } from "react-hook-form";

import { WorkflowPostponedActivityReadDtoActivityEnum } from "@/config/apiClient";
import { useOrderItems, useToasts } from "@/hooks";
import { useOrderItemProofs } from "@/hooks/queries/useOrderItemProofs";
import { useOrderItemQuotes } from "@/hooks/queries/useOrderItemQuotes";

import {
  type FormValues,
  type ModalWorkflowLoopBackProps,
} from "./ModalWorkflowLoopBack.types";

export const ModalWorkflowLoopBack = (props: ModalWorkflowLoopBackProps) => {
  const { t } = useTranslate();
  const { add } = useToasts();
  const methods = useForm<FormValues>({ defaultValues: { comment: "" } });

  const { useCancelAllCurrentOrderItemQuotes } = useOrderItemQuotes();
  const { useCancelOrderItemProof } = useOrderItemProofs();
  const { useGetAffectedOrderItemsByQuoteCancellation } = useOrderItems();

  const { data: affectedOrderItemsByQuoteCancellationData } =
    useGetAffectedOrderItemsByQuoteCancellation({
      orderItemId: props.orderItem.id,
      orderId: props.orderItem.orderId,
      isEnabled: props.isOpen,
    });

  const isCancelQuote =
    props.activity === WorkflowPostponedActivityReadDtoActivityEnum.CancelQuote;
  const isCancelProof =
    props.activity === WorkflowPostponedActivityReadDtoActivityEnum.CancelProof;

  const { mutateAsync: cancelQuoteAsync, isPending: isPendingQuote } =
    useCancelAllCurrentOrderItemQuotes();
  const { mutateAsync: cancelProofAsync, isPending: isPendingProof } =
    useCancelOrderItemProof();

  const handleCancel = async (formData: FormValues) => {
    try {
      if (isCancelQuote) {
        await cancelQuoteAsync({
          orderId: props.orderItem.orderId,
          orderItemId: props.orderItem.id,
          cancelReason: formData.comment,
        });
      }

      const orderItem = props.orderItem;
      const orderedSandboxItemProofs =
        "SandboxItemProof" in orderItem.sandboxItem
          ? orderItem.sandboxItem.SandboxItemProof.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )
          : [];

      if (isCancelProof && orderedSandboxItemProofs[0].id) {
        await cancelProofAsync({
          orderItemId: props.orderItem.id,
          orderId: props.orderItem.orderId,
          sandboxItemProofCancelCommand: {
            isCancelled: true,
            isValidated: false,
            cancelReason: formData.comment,
            versions: [],
          },
          sandboxItemProofId: orderedSandboxItemProofs[0].id,
        });
      }
      add({
        state: "success",
        title: t("cardCancelQuote.toast.success"),
      });
      props.setIsOpen(false);
    } catch (_error) {
      add({
        state: "error",
        title: t("cardCancelQuote.toast.error"),
      });
      props.setIsOpen(false);
    }
  };

  const isPending = isPendingQuote || isPendingProof;

  const otherAffectedOrderItems =
    affectedOrderItemsByQuoteCancellationData?.data.affectedOrderItems.filter(
      (oi) => oi.id !== props.orderItem.id
    );
  return (
    <FormProvider {...methods}>
      <KdsModal
        isOpen={props.isOpen}
        onOpenChange={() => props.setIsOpen(!props.isOpen)}
        size="sm"
        dialogProps={{
          dialogHeaderProps: {
            title: t("cardCancelQuote.title"),
          },
          content: (
            <div className="flex flex-col gap-sp-md">
              {/* Warning section */}
              {otherAffectedOrderItems &&
                otherAffectedOrderItems?.length > 0 && (
                  <KdsCallout
                    state="warning"
                    iconLeft={RiErrorWarningLine}
                    title={t("cardCancelQuote.loopback.warning", {
                      count: otherAffectedOrderItems.length,
                      productNames: `"${otherAffectedOrderItems.map((oi) => oi.sandboxItem.name).join(", ")}"`,
                    })}
                    subtitle={t("cardCancelQuote.loopback.warningSubtitle", {
                      count: otherAffectedOrderItems.length,
                    })}
                  />
                )}

              <div className="flex flex-col gap-sp-xs">
                <KdsText size="title-sm">
                  {t("cardCancelQuote.loopback.subtitle")}
                </KdsText>
                <KdsText>{t("cardCancelQuote.loopback.description")}</KdsText>
              </div>

              <KdsTextArea
                label={t("cardCancelQuote.loopback.comment")}
                {...methods.register("comment")}
                autoFocus
                errorMessage={t("cardCancelQuote.loopback.commentError")}
                hasError={!!methods.formState.errors.comment}
                placeholder={t("cardCancelQuote.loopback.commentPlaceholder")}
              />
            </div>
          ),
          dialogFooterProps: {
            buttonProps: [
              {
                children: t("buttons.cancel"),
                color: "neutral",
                variant: "ghost",
                isLoading: isPending,
                disabled: isPending,
                onPress: () => props.setIsOpen(false),
              },
              {
                children: t("buttons.reset"),
                isLoading: isPending,
                disabled: isPending,
                dataCy: "submitCancelQuoteButton",
                onPress: () => void methods.handleSubmit(handleCancel)(),
              },
            ],
            type: "double",
          },
          overflow: false,
        }}
      />
    </FormProvider>
  );
};
